import { cn } from '@repo/ui/lib'
import { SortDirection } from '@tanstack/react-table'
import * as React from 'react'

const Table = React.forwardRef<HTMLTableElement, React.HTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    <div className="relative w-full overflow-auto">
      <table className={cn('w-full caption-bottom text-sm', className)} ref={ref} {...props} />
    </div>
  ),
)
Table.displayName = 'Table'

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead className={cn('[&_tr]:border-b', className)} ref={ref} {...props} />
))
TableHeader.displayName = 'TableHeader'

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody className={cn('[&_tr:last-child]:border-0', className)} ref={ref} {...props} />
))
TableBody.displayName = 'TableBody'

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    className={cn('border-t bg-muted/50 font-medium [&>tr]:last:border-b-0', className)}
    ref={ref}
    {...props}
  />
))
TableFooter.displayName = 'TableFooter'

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr
      className={cn(
        'transition-colors data-[state=selected]:bg-muted',
        'hover:bg-[#F4F7FA] hover:dark:bg-[#333333]',
        'cursor-pointer border-b border-gray-200 dark:border-gray-800',
        className,
      )}
      ref={ref}
      {...props}
    />
  ),
)
TableRow.displayName = 'TableRow'

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement> & {
    isCenter?: boolean
    isSortable?: boolean
    sortState?: false | SortDirection
  }
>(({ children, className, isCenter = false, isSortable = false, sortState, ...props }, ref) => (
  <th
    className={cn(
      'uppercase',
      'h-10 px-4 text-left align-middle text-xs font-medium text-[#212B36] dark:text-[#b6c4d2] [&:has([role=checkbox])]:pr-0',
      isSortable && 'cursor-pointer hover:bg-gray-200',
      className,
    )}
    ref={ref}
    {...props}
  >
    <div className={cn('flex', isCenter ? 'justify-center' : 'justify-start')}>
      {children}
      {isSortable && sortState && (
        <p className={cn('ml-2 text-left text-gray-500')}>{sortState === 'asc' ? '↑' : '↓'}</p>
      )}
    </div>
  </th>
))
TableHead.displayName = 'TableHead'

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => {
  'use memo'
  return (
    <td
      className={cn('p-4 text-left align-middle [&:has([role=checkbox])]:pr-0', className)}
      ref={ref}
      {...props}
    />
  )
})
TableCell.displayName = 'TableCell'

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption className={cn('mt-4 text-sm text-muted-foreground', className)} ref={ref} {...props} />
))
TableCaption.displayName = 'TableCaption'

export { Table, TableBody, TableCaption, TableCell, TableFooter, TableHead, TableHeader, TableRow }
